import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Header from "../Header";
import Footer from "../Footer";
import useSeo from "../../hooks/useSeo";
import { assetUrl } from "../../utils/api";
import { secondhandGet } from "../../api/secondhandApi";
import GoodsChannelTabs from "../shop/GoodsChannelTabs";

export default function SecondhandListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const userId = Number(localStorage.getItem("user_id") || 0);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [keyword, setKeyword] = useState(searchParams.get("keyword") || "");
  const [cityInput, setCityInput] = useState(searchParams.get("city") || "");

  const category = searchParams.get("category") || "";
  const city = searchParams.get("city") || "";
  const intent = searchParams.get("intent") || "";
  const page = Number(searchParams.get("page") || 1);

  useSeo({
    title: "闲置交换 - 生活好物",
    metaDescription: "会员闲置交换：旧书、日用品同城互助，私信沟通后线下交接。",
    path: "/secondhand",
    keywords: "闲置交换,二手,旧书,同城",
  });

  useEffect(() => {
    setLoading(true);
    setError("");
    const params = new URLSearchParams({ page: String(page), page_size: "20" });
    if (keyword.trim()) params.set("keyword", keyword.trim());
    if (category) params.set("category", category);
    if (city.trim()) params.set("city", city.trim());
    if (intent) params.set("intent", intent);

    secondhandGet(`/listings?${params}`)
      .then(setData)
      .catch((e) => setError(e.message || "加载失败"))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, category, city, intent, searchParams.get("keyword")]);

  const onSearch = (e) => {
    e.preventDefault();
    const next = new URLSearchParams(searchParams);
    if (keyword.trim()) next.set("keyword", keyword.trim());
    else next.delete("keyword");
    next.delete("page");
    setSearchParams(next);
  };

  const setFilter = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    next.delete("page");
    setSearchParams(next);
  };

  const items = data?.items || [];
  const meta = data?.meta;
  const totalPages = Math.max(1, Math.ceil((data?.total || 0) / 20));

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-50 to-amber-50 flex flex-col">
      <Header />
      <main className="flex-1 px-4 py-8">
        <div className="max-w-5xl mx-auto space-y-6">
          <header className="text-center space-y-3">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-800">生活好物</h1>
            <p className="text-gray-500 text-sm">同城闲置互助：旧书、日用品，私信沟通后线下交接</p>
            <GoodsChannelTabs />
          </header>

          <div className="flex flex-wrap justify-center gap-2">
            <Link
              to={userId ? "/secondhand/new" : `/login?redirect=${encodeURIComponent("/secondhand/new")}`}
              className="px-5 py-2.5 rounded-full bg-rose-600 text-white text-sm font-medium"
            >
              发布闲置
            </Link>
            {userId ? (
              <Link
                to="/secondhand/mine"
                className="px-5 py-2.5 rounded-full border border-rose-200 text-rose-700 text-sm bg-white"
              >
                我的发布
              </Link>
            ) : null}
          </div>

          <form onSubmit={onSearch} className="max-w-xl mx-auto flex gap-2">
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索标题或描述…"
              className="flex-1 border border-pink-100 rounded-full px-4 py-2.5 bg-white text-sm"
            />
            <button type="submit" className="px-5 py-2.5 rounded-full bg-pink-500 text-white text-sm">
              搜索
            </button>
          </form>

          <div className="flex flex-wrap justify-center gap-2 items-center">
            <form
              className="flex gap-1"
              onSubmit={(e) => {
                e.preventDefault();
                setFilter("city", cityInput.trim());
              }}
            >
              <input
                value={cityInput}
                onChange={(e) => setCityInput(e.target.value)}
                placeholder="城市，如合肥"
                className="border border-pink-100 rounded-full px-3 py-1.5 text-sm bg-white w-36"
              />
              <button type="submit" className="px-3 py-1.5 rounded-full border text-sm bg-white">
                同城
              </button>
            </form>
            {(meta?.categories || []).map((c) => (
              <button
                key={c.value}
                type="button"
                onClick={() => setFilter("category", category === c.value ? "" : c.value)}
                className={`px-3 py-1.5 rounded-full text-sm border ${
                  category === c.value
                    ? "bg-pink-500 text-white border-pink-500"
                    : "bg-white text-gray-600 border-gray-200"
                }`}
              >
                {c.label}
              </button>
            ))}
            {(meta?.intents || []).map((c) => (
              <button
                key={c.value}
                type="button"
                onClick={() => setFilter("intent", intent === c.value ? "" : c.value)}
                className={`px-3 py-1.5 rounded-full text-sm border ${
                  intent === c.value
                    ? "bg-amber-500 text-white border-amber-500"
                    : "bg-white text-gray-600 border-gray-200"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>

          {error && (
            <div className="text-center text-red-500 text-sm bg-red-50 rounded-xl py-3">{error}</div>
          )}

          {loading ? (
            <div className="text-center text-gray-400 py-16">加载中...</div>
          ) : items.length === 0 ? (
            <div className="text-center text-gray-400 py-16 bg-white rounded-2xl">暂无闲置，来发布第一条吧</div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {items.map((item) => (
                <Link
                  key={item.id}
                  to={`/secondhand/${item.id}`}
                  className="bg-white rounded-2xl border border-pink-50 overflow-hidden hover:shadow-md transition"
                >
                  <div className="aspect-[4/3] bg-rose-50">
                    {item.cover_url ? (
                      <img
                        src={assetUrl(item.cover_url)}
                        alt={item.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-300 text-sm">
                        暂无图片
                      </div>
                    )}
                  </div>
                  <div className="p-4 space-y-1">
                    <h2 className="font-semibold text-gray-800 line-clamp-1">{item.title}</h2>
                    <p className="text-xs text-gray-400">
                      {item.location_label || "城市未填"} · {item.intent_label}
                    </p>
                    <p className="text-sm text-pink-600">{item.price_note || item.condition_label}</p>
                  </div>
                </Link>
              ))}
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex justify-center gap-2 text-sm">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => {
                  const next = new URLSearchParams(searchParams);
                  next.set("page", String(page - 1));
                  setSearchParams(next);
                }}
                className="px-3 py-1.5 rounded-lg border disabled:opacity-40 bg-white"
              >
                上一页
              </button>
              <span className="px-2 py-1.5 text-gray-500">
                {page}/{totalPages}
              </span>
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => {
                  const next = new URLSearchParams(searchParams);
                  next.set("page", String(page + 1));
                  setSearchParams(next);
                }}
                className="px-3 py-1.5 rounded-lg border disabled:opacity-40 bg-white"
              >
                下一页
              </button>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
